import React from 'react';
import { Link } from 'react-router-dom';
import { useConfig } from '../context/ConfigContext';
import { SUPPORT_EMAIL } from '../config/support';

/**
 * LegalLayout - shared chrome for the public legal pages (/privacy, /terms,
 * /delete-account). Public and unauthenticated by design: both app stores
 * require these pages to be reachable without logging in.
 *
 * @param {string} title - page heading
 * @param {string} subtitle - one-line summary under the heading
 * @param {string} updated - human-readable last-updated date
 * @param {React.ReactNode} children - page body
 */
function LegalLayout({ title, subtitle, updated, children }) {
  const { getAppName } = useConfig();

  return (
    <div className="legal-page">
      <header className="legal-header">
        <div className="legal-header-content">
          <Link to="/" className="legal-logo">
            <span className="legal-logo-icon">&#10052;&#65039;</span>
            <span className="legal-logo-text">{getAppName()}</span>
          </Link>
          <nav className="legal-nav">
            <Link to="/pricing">Pricing</Link>
            <Link to="/login">Log In</Link>
          </nav>
        </div>
      </header>

      <main className="legal-main">
        <h1 className="legal-title">{title}</h1>
        {subtitle && <p className="legal-subtitle">{subtitle}</p>}
        {updated && <p className="legal-updated">Last updated: {updated}</p>}
        <div className="legal-body">{children}</div>
      </main>

      <footer className="legal-footer">
        <div className="legal-footer-links">
          <Link to="/privacy">Privacy Policy</Link>
          <Link to="/terms">Terms of Service</Link>
          <Link to="/delete-account">Delete My Account</Link>
          <a href={`mailto:${SUPPORT_EMAIL}`}>{SUPPORT_EMAIL}</a>
        </div>
        <p className="legal-copy">
          &copy; {new Date().getFullYear()} {getAppName()}. All rights reserved.
        </p>
      </footer>

      <style>{`
        .legal-page {
          min-height: 100vh;
          background: #ffffff;
          display: flex;
          flex-direction: column;
        }

        .legal-header {
          background: white;
          border-bottom: 1px solid #e5e7eb;
          position: sticky;
          top: 0;
          z-index: 100;
        }

        .legal-header-content {
          max-width: 820px;
          margin: 0 auto;
          padding: 16px 24px;
          display: flex;
          justify-content: space-between;
          align-items: center;
          gap: 16px;
        }

        .legal-logo {
          display: flex;
          align-items: center;
          gap: 8px;
          text-decoration: none;
        }

        .legal-logo-icon {
          font-size: 1.4rem;
        }

        .legal-logo-text {
          font-size: 1.2rem;
          font-weight: 700;
          color: var(--color-primary);
        }

        .legal-nav {
          display: flex;
          gap: 20px;
        }

        .legal-nav a {
          color: #374151;
          text-decoration: none;
          font-size: 0.95rem;
          font-weight: 500;
        }

        .legal-nav a:hover {
          color: var(--color-primary);
        }

        .legal-main {
          flex: 1;
          max-width: 820px;
          margin: 0 auto;
          padding: 48px 24px 64px;
          width: 100%;
          box-sizing: border-box;
        }

        .legal-title {
          font-size: 2.25rem;
          font-weight: 800;
          color: #111827;
          letter-spacing: -0.02em;
          margin: 0 0 12px;
        }

        .legal-subtitle {
          font-size: 1.1rem;
          color: #4b5563;
          line-height: 1.6;
          margin: 0 0 8px;
        }

        .legal-updated {
          font-size: 0.875rem;
          color: #9ca3af;
          margin: 0 0 40px;
        }

        .legal-body {
          color: #374151;
          font-size: 1rem;
          line-height: 1.7;
        }

        .legal-body h2 {
          font-size: 1.35rem;
          font-weight: 700;
          color: #111827;
          margin: 40px 0 12px;
          padding-top: 8px;
        }

        .legal-body h3 {
          font-size: 1.05rem;
          font-weight: 700;
          color: #111827;
          margin: 24px 0 8px;
        }

        .legal-body p {
          margin: 0 0 16px;
        }

        .legal-body ul {
          margin: 0 0 16px;
          padding-left: 22px;
        }

        .legal-body li {
          margin-bottom: 8px;
        }

        .legal-body a {
          color: var(--color-primary);
          text-decoration: underline;
        }

        .legal-body strong {
          color: #111827;
        }

        .legal-callout {
          background: #f0f7ff;
          border: 1px solid #bfdbfe;
          border-left: 4px solid var(--color-primary);
          border-radius: 8px;
          padding: 16px 20px;
          margin: 0 0 24px;
        }

        .legal-callout p:last-child {
          margin-bottom: 0;
        }

        .legal-table-wrapper {
          overflow-x: auto;
          margin: 0 0 24px;
        }

        .legal-table {
          width: 100%;
          border-collapse: collapse;
          font-size: 0.9rem;
          min-width: 520px;
        }

        .legal-table th,
        .legal-table td {
          text-align: left;
          padding: 10px 12px;
          border-bottom: 1px solid #e5e7eb;
          vertical-align: top;
        }

        .legal-table th {
          background: #f9fafb;
          font-weight: 700;
          color: #111827;
        }

        .legal-footer {
          border-top: 1px solid #e5e7eb;
          padding: 32px 24px;
          text-align: center;
          background: #f9fafb;
        }

        .legal-footer-links {
          display: flex;
          gap: 24px;
          justify-content: center;
          flex-wrap: wrap;
          margin-bottom: 12px;
        }

        .legal-footer-links a {
          color: #4b5563;
          text-decoration: none;
          font-size: 0.9rem;
        }

        .legal-footer-links a:hover {
          color: var(--color-primary);
          text-decoration: underline;
        }

        .legal-copy {
          color: #9ca3af;
          font-size: 0.85rem;
          margin: 0;
        }

        @media (max-width: 640px) {
          .legal-main {
            padding: 32px 16px 48px;
          }

          .legal-title {
            font-size: 1.75rem;
          }

          .legal-subtitle {
            font-size: 1rem;
          }

          .legal-body h2 {
            font-size: 1.2rem;
            margin-top: 32px;
          }

          .legal-header-content {
            padding: 14px 16px;
          }

          .legal-nav {
            gap: 14px;
          }
        }
      `}</style>
    </div>
  );
}

export default LegalLayout;
